import TagManager from "react-gtm-module";
import { getProductBySku } from "./ProductUtils";
import { ecommerceCategories } from "./ProductCategoryUtils";
import { Product } from "../types/Product";
import { ProductCategory } from "../types/ProductCategory";

export function gtmSendEvent(event: unknown) {
  if (event !== null) {
    TagManager.dataLayer({
      dataLayer: event
    });
  }
}

export function itemFromProduct(
  sku: string,
  product: Product | undefined,
  quantity: number | undefined,
  productCategories: ProductCategory[],
  orderLine?: OrderLineDto
) {
  const isSample = product ? product.sku !== sku : !!orderLine?.mainArticleSku;
  if (!productCategories) {
    console.error("itemFromProduct called without productCategories!");
  }
  const itemCategories = isSample
    ? ["samples"]
    : ecommerceCategories(product, productCategories);
  if (itemCategories.length === 0) {
    console.warn("itemFromProduct had no productCategories!");
    itemCategories.push("products");
  }
  const productName = product?.name || orderLine?.name || sku;
  const item = {
    item_id: sku,
    item_name: isSample ? "Sample: " + productName : productName,
    item_brand: product?.brand,
    item_category: itemCategories[0],
    item_category2: itemCategories[1],
    item_category3: itemCategories[2],
    item_category4: itemCategories[3],
    item_category5: itemCategories[4],
    currency: orderLine?.price?.currency || product?.price?.currency,
    quantity,
    price:
      isSample || (!product?.price && !orderLine?.price)
        ? 0
        : parseFloat(
            orderLine?.price?.basePrice || product?.price?.basePrice || "0"
          )
  };
  return JSON.parse(JSON.stringify(item)); // to remove undefined properties
}

export function beginCheckoutEventFromOrderData(
  orderData: {
    campaignCode: string;
    cart: { sku: string; amount: number }[];
  },
  products: Product[],
  productCategories: ProductCategory[]
) {
  return {
    event: "begin_checkout",
    ecommerce: {
      coupon: orderData.campaignCode,
      items: orderData.cart.map(({ sku, amount }) => {
        const product = getProductBySku(products, sku);
        return itemFromProduct(sku, product, amount, productCategories);
      })
    }
  };
}

export function purchaseEventFromOrder(
  order: OrderDto,
  products: Product[],
  productCategories: ProductCategory[]
) {
  let currency;
  const items = order.lines.map(line => {
    const { sku, amount } = line;
    const product = getProductBySku(products, sku);
    const item = itemFromProduct(sku, product, amount, productCategories, line);
    currency = item.currency;
    return item;
  });
  return {
    event: "purchase",
    ecommerce: {
      transaction_id: order.orderUuid,
      affiliation: "Bjelin Web",
      items,
      currency,
      value:
        parseFloat(order.totals.baseSubTotal) +
        parseFloat(order.totals.baseTotalDiscount),
      tax: parseFloat(order.totals.totalVat),
      shipping: parseFloat(order.totals.baseShippingFee),
      discount: parseFloat(order.totals.baseTotalDiscount),
      coupon: order.discountCode
    }
  };
}

export function gtmSendViewItem(
  product: Product,
  productCategories: ProductCategory[]
) {
  gtmSendEvent({
    event: "view_item",
    ecommerce: {
      items: [
        itemFromProduct(product.sku, product, undefined, productCategories)
      ]
    }
  });
}

export function updateCartEvent(
  sku: string,
  product: Product,
  oldAmount: number,
  newAmount: number,
  productCategories: ProductCategory[]
) {
  const diff = newAmount - oldAmount;
  if (diff === 0) {
    return null;
  }
  return {
    event: diff > 0 ? "add_to_cart" : "remove_from_cart",
    ecommerce: {
      items: [itemFromProduct(sku, product, Math.abs(diff), productCategories)]
    }
  };
}

export function gtmSendUpdateCart(
  sku: string,
  product: Product,
  oldAmount: number,
  newAmount: number,
  productCategories: ProductCategory[]
) {
  const event = updateCartEvent(
    sku,
    product,
    oldAmount,
    newAmount,
    productCategories
  );
  gtmSendEvent(event);
}
